/* ══════════════════════════════════════════════════════════════════════
   Splash — site header behaviour

     • sticky header state on scroll
     • Ask Ripple search → Splash.Ripple.query()
     • ask ripple button → Splash.Ripple.open()
     • mobile menu drawer
     • Splash.Header.setCartCount(n) for cart updates elsewhere

   Colour mode is NOT handled here: it is a merchant setting rendered
   server-side onto <html> in the layout, so there is nothing to toggle,
   store or re-sync on the client.

   Public API
     Splash.Header.setCartCount(n)
   ══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var Splash = (window.Splash = window.Splash || {});

  /* ── Header ─────────────────────────────────────────────────────── */

  function initHeader(header) {
    if (!header || header.dataset.spInit === 'true') return;
    header.dataset.spInit = 'true';

    /* Publish the header's height so full-height sections below it can
       subtract it: min-height: calc(100dvh - var(--sp-header-height)).

       Only ever publish the EXPANDED height. Publishing the collapsed one
       would grow a hero sized against this variable, which moves the hero's
       bottom edge, which re-triggers the collapse observer — a loop. */
    function publishHeight() {
      if (header.getAttribute('data-collapsed') === 'true') return;
      document.documentElement.style.setProperty(
        '--sp-header-height',
        header.offsetHeight + 'px'
      );
    }
    publishHeight();
    if (window.ResizeObserver) {
      new ResizeObserver(publishHeight).observe(header);
    } else {
      window.addEventListener('resize', publishHeight);
    }

    /* Sticky state — drives the shadow and the optional bar collapse. */
    function applyScrolled() {
      header.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
    }
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        applyScrolled();
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    /* Set the initial state directly: in a background tab rAF is paused,
       and routing the first call through the gate would latch `ticking`. */
    applyScrolled();

    /* Ask Ripple search field. Without JS the form falls through to the
       native storefront search, so the input is never a dead end. */
    var search = header.querySelector('[data-ripple-search]');
    if (search) {
      var input = search.querySelector('input[type="search"], input[type="text"]');
      search.addEventListener('submit', function (event) {
        if (!Splash.Ripple) return; /* let the native search handle it */
        event.preventDefault();
        var value = input ? input.value : '';
        if (!value.trim()) {
          if (input) input.focus();
          return;
        }
        Splash.Ripple.query(value, { source: 'header-search' });
      });
    }

    /* ask ripple button → the full chat surface. */
    var cta = header.querySelector('[data-ripple-open]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        event.preventDefault();
        if (Splash.Ripple) Splash.Ripple.open({ source: 'header-cta' });
      });
    }

    initCollapse(header);
    initMenu(header);
  }

  /* ── Sticky collapse ────────────────────────────────────────────────
     Past the hero, the utility bar and tier two fold away and tier one
     slims down; it restores when the hero is back in view.

     The hero is whatever a template marks with data-header-collapse-anchor,
     else the first element in <main>. With no anchor and no
     IntersectionObserver we fall back to a plain scroll threshold. */

  /* A section's leading <style>/<link> tags are real elements with no
     layout box — observing one would report "never intersecting" and pin
     the header collapsed from first paint. Take the first child that
     actually occupies space. */
  function findCollapseAnchor() {
    var explicit = document.querySelector('[data-header-collapse-anchor]');
    if (explicit) return explicit;

    var main = document.querySelector('main') || document.body;
    var children = main.children;
    for (var i = 0; i < children.length; i++) {
      if (children[i].getBoundingClientRect().height > 0) return children[i];
    }
    return null;
  }

  function initCollapse(header) {
    if (header.getAttribute('data-collapse-on-scroll') === 'false') return;

    var anchor = findCollapseAnchor();

    function setCollapsed(on) {
      if (header.getAttribute('data-collapsed') === String(on)) return;
      header.setAttribute('data-collapsed', on ? 'true' : 'false');
    }

    if (anchor && window.IntersectionObserver) {
      new IntersectionObserver(
        function (entries) {
          setCollapsed(!entries[0].isIntersecting);
        },
        { threshold: 0 }
      ).observe(anchor);
      return;
    }

    /* Fallback: collapse past roughly one hero's worth of scrolling. */
    var FALLBACK_PX = 240;
    var pending = false;
    window.addEventListener(
      'scroll',
      function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          setCollapsed(window.scrollY > FALLBACK_PX);
          pending = false;
        });
      },
      { passive: true }
    );
  }

  /* ── Mobile menu ────────────────────────────────────────────────── */

  function initMenu(header) {
    var menu = document.getElementById(header.dataset.menuId || 'sp-menu');
    var trigger = header.querySelector('[data-menu-open]');
    if (!menu || !trigger) return;

    /* Focus stays inside the panel — the scrim is decorative. */
    var panel = menu.querySelector('.sp-menu__panel') || menu;
    var lastFocused = null;

    function open() {
      lastFocused = document.activeElement;
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      var first = panel.querySelector('a, button');
      if (first) first.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      /* Keep focus inside the drawer while it is modal. */
      var focusables = panel.querySelectorAll('a[href], button:not([disabled])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    trigger.addEventListener('click', function () {
      if (menu.hidden) open();
      else close();
    });

    menu.addEventListener('click', function (event) {
      if (event.target.closest('[data-menu-close]')) close();
    });

    /* Close if the viewport grows back into the desktop layout. */
    if (window.matchMedia) {
      var desktop = window.matchMedia('(min-width: 1024px)');
      var onChange = function (event) {
        if (event.matches && !menu.hidden) close();
      };
      if (desktop.addEventListener) desktop.addEventListener('change', onChange);
      else if (desktop.addListener) desktop.addListener(onChange);
    }
  }

  /* ── Cart count ─────────────────────────────────────────────────── */

  Splash.Header = Splash.Header || {};
  Splash.Header.setCartCount = function (count) {
    var badges = document.querySelectorAll('[data-cart-count]');
    var value = Number(count) || 0;
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = value > 99 ? '99+' : String(value);
      badges[i].hidden = value === 0;
      var button = badges[i].closest('a, button');
      if (button) {
        button.setAttribute(
          'aria-label',
          'Cart, ' + value + (value === 1 ? ' item' : ' items')
        );
      }
    }
  };

  /* ── Boot ───────────────────────────────────────────────────────── */

  function boot() {
    var headers = document.querySelectorAll('[data-splash-header]');
    for (var i = 0; i < headers.length; i++) initHeader(headers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', boot);
})();
